import { html, nothing } from "lit";
import type { SkillMessageMap } from "../controllers/skills.js";
import type { SkillStatusReport, ChannelsStatusSnapshot } from "../types.js";
import { EmployeeRow } from "./employees.js";

/**
 * Props for the Employee Detail View.
 */
export type EmployeeDetailProps = {
  employee: EmployeeRow;
  skillsReport: SkillStatusReport | null;
  skillMessages: SkillMessageMap;
  channelsSnapshot: ChannelsStatusSnapshot | null;
  accountOptions: {
    authProfiles: Array<{
      profileId: string;
      provider?: string;
      mode?: string;
      label?: string;
      available?: boolean;
    }>;
    channelAccounts: Array<{
      channelId: string;
      accountId: string;
      label?: string;
      isDefault?: boolean;
    }>;
  } | null;
  onClose: () => void;
  onUpdate: (data: Partial<EmployeeRow["employee"]>) => void;
  onDelete: () => void;
  onAddChannel: (channelId: string) => void;
  onRemoveChannel: (channelId: string) => void;
  onToggleSkill: (skillKey: string, enabled: boolean) => void;
};

/**
 * Render the full-page Employee Detail View.
 */
export function renderEmployeeDetailView(props: EmployeeDetailProps) {
  const {
    employee: row,
    skillsReport,
    skillMessages,
    channelsSnapshot,
    accountOptions,
    onClose,
    onUpdate,
    onDelete,
    onAddChannel,
    onRemoveChannel,
    onToggleSkill,
  } = props;
  const { employee: e } = row;
  const accountEntries = Object.entries(e.accounts ?? {});
  const authProfiles = accountOptions?.authProfiles ?? [];
  const channelAccounts = accountOptions?.channelAccounts ?? [];
  const authProfileHints = authProfiles.map((profile) => profile.label ?? profile.profileId);
  const channelAccountHints = channelAccounts.map(
    (account) =>
      `${account.label ?? account.channelId}:${account.accountId}${account.isDefault ? " (default)" : ""}`,
  );
  const resolveProfileProvider = (profile: { profileId: string; provider?: string }) =>
    profile.provider ?? profile.profileId.split(":")[0] ?? profile.profileId;

  const providerOptions = Array.from(
    new Set([
      ...authProfiles.map((profile) => resolveProfileProvider(profile)),
      ...channelAccounts.map((account) => account.channelId),
    ]),
  ).filter(Boolean);
  const providerListId = `employee-detail-${e.employeeId}-providers`;
  const accountListId = `employee-detail-${e.employeeId}-channel-accounts`;
  const profileListId = `employee-detail-${e.employeeId}-auth-profiles`;

  const resolveDefaultsForProvider = (provider: string) => {
    const matchedAccount =
      channelAccounts.find((account) => account.channelId === provider && account.isDefault) ??
      channelAccounts.find((account) => account.channelId === provider);
    const matchedProfile = authProfiles.find(
      (profile) => resolveProfileProvider(profile) === provider,
    );
    return {
      accountId: matchedAccount?.accountId,
      profileId: matchedProfile?.profileId ?? matchedAccount?.accountId,
      label: matchedProfile?.label ?? matchedAccount?.label,
    };
  };

  const applyAccountSelection = (
    container: HTMLElement | null,
    selection: {
      provider?: string;
      accountId?: string;
      profileId?: string;
      label?: string;
    },
  ) => {
    if (!container) {
      return;
    }
    const providerInput = container.querySelector<HTMLInputElement>("[data-account-provider]");
    const accountInput = container.querySelector<HTMLInputElement>("[data-account-id]");
    const profileInput = container.querySelector<HTMLInputElement>("[data-profile-id]");
    const labelInput = container.querySelector<HTMLInputElement>("[data-account-label]");

    if (providerInput && selection.provider) {
      providerInput.value = selection.provider;
    }
    if (accountInput) {
      accountInput.value = selection.accountId ?? "";
    }
    if (profileInput) {
      profileInput.value = selection.profileId ?? selection.accountId ?? "";
    }
    if (labelInput) {
      labelInput.value = selection.label ?? "";
    }
  };

  const matchesChannelAccount = (provider: string, accountId: string) =>
    channelAccounts.some(
      (account) => account.channelId === provider && account.accountId === accountId,
    );

  const validateAccountSelection = (params: {
    provider: string;
    accountId: string;
    profileId: string;
  }) => {
    const providerKnown = providerOptions.includes(params.provider);
    const profileKnown = authProfiles.some((profile) => profile.profileId === params.profileId);
    const accountKnown = params.accountId
      ? matchesChannelAccount(params.provider, params.accountId)
      : true;
    const profileMatchesChannel = matchesChannelAccount(params.provider, params.profileId);

    if (providerOptions.length > 0 && !providerKnown) {
      return confirm(`Provider "${params.provider}" is not in the known list. Continue anyway?`);
    }
    if (!profileKnown && !profileMatchesChannel) {
      return confirm(
        `Profile "${params.profileId}" is not in auth profiles or channel accounts. Continue anyway?`,
      );
    }
    if (!accountKnown) {
      return confirm(
        `Account "${params.accountId}" is not listed for ${params.provider}. Continue anyway?`,
      );
    }
    return true;
  };

  const saveAccountBinding = (params: {
    provider: string;
    accountId: string;
    profileId: string;
    label: string;
  }) => {
    const nextAccounts = { ...e.accounts };
    nextAccounts[params.provider] = {
      profileId: params.profileId,
      accountId: params.accountId || undefined,
      label: params.label || undefined,
    };
    onUpdate({ accounts: nextAccounts });
  };

  return html`
    <div class="employee-detail-page">
      <header class="content-header" style="margin-bottom: 24px;">
        <div style="display: flex; align-items: center; gap: 16px;">
          <button class="btn-icon" @click=${onClose} title="Back to list">
            ←
          </button>
          <div>
            <div class="page-title">${e.name}</div>
            <div class="page-sub">Employee ID: ${e.employeeId} • Agent: ${row.agentId}</div>
          </div>
        </div>
        <div class="page-meta">
          <button class="btn danger" @click=${() => {
            if (confirm(`Are you sure you want to delete employee "${e.name}"?`)) {
              onDelete();
            }
          }}>
            Delete Employee
          </button>
        </div>
      </header>

      <div class="employee-detail-grid" style="display: grid; grid-template-columns: 1fr 350px; gap: 24px;">
        <div class="employee-detail-main">
          <!-- Basic Info Card -->
          <div class="card" style="margin-bottom: 24px;">
            <div class="card-header">
              <h3 style="margin: 0; font-size: 16px;">Basic Information</h3>
            </div>
            <div class="card-body" style="padding: 20px; display: grid; grid-template-columns: 1fr 1fr; gap: 20px;">
              <div class="field">
                <label>Full Name</label>
                <input type="text" .value=${e.name} @change=${(ev: Event) => onUpdate({ name: (ev.target as HTMLInputElement).value })} />
              </div>
              <div class="field">
                <label>Email Address</label>
                <input type="email" .value=${e.email} @change=${(ev: Event) => onUpdate({ email: (ev.target as HTMLInputElement).value })} />
              </div>
              <div class="field">
                <label>Department</label>
                <input type="text" .value=${e.department ?? ""} @change=${(ev: Event) => onUpdate({ department: (ev.target as HTMLInputElement).value || undefined })} />
              </div>
              <div class="field">
                <label>Role / Title</label>
                <input type="text" .value=${e.role ?? ""} @change=${(ev: Event) => onUpdate({ role: (ev.target as HTMLInputElement).value || undefined })} />
              </div>
              <div class="field">
                <label>Status</label>
                <select .value=${e.status} @change=${(ev: Event) => onUpdate({ status: (ev.target as HTMLSelectElement).value as EmployeeRow["employee"]["status"] })}>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="onboarding">Onboarding</option>
                  <option value="terminated">Terminated</option>
                </select>
              </div>
              <div class="field">
                <label>Start Date</label>
                <input type="date" .value=${e.startDate ?? ""} @change=${(ev: Event) => onUpdate({ startDate: (ev.target as HTMLInputElement).value || undefined })} />
              </div>
            </div>
          </div>

          <!-- Channel Access Card -->
          <div class="card" style="margin-bottom: 24px;">
            <div class="card-header" style="display: flex; justify-content: space-between; align-items: center;">
              <h3 style="margin: 0; font-size: 16px;">Channel Access</h3>
            </div>
            <div class="card-body" style="padding: 20px;">
              <p class="muted" style="margin-bottom: 16px;">Configure which messaging channels this employee can use.</p>
              
              <div style="margin-bottom: 20px;">
                <div style="font-size: 13px; font-weight: 600; margin-bottom: 10px;">Active Channels</div>
                <div class="chip-row" style="display: flex; flex-wrap: wrap; gap: 8px;">
                  ${(e.channels?.allowed ?? []).map(
                    (channel) => html`
                    <span class="chip" style="display: flex; align-items: center; gap: 8px; padding: 6px 12px; background: var(--secondary); border-radius: 16px;">
                      ${channel}
                      <button style="border: none; background: none; cursor: pointer; padding: 0; color: var(--muted);" @click=${() => onRemoveChannel(channel)}>×</button>
                    </span>
                  `,
                  )}
                  ${
                    (e.channels?.allowed ?? []).length === 0
                      ? html`
                          <div class="muted">No channels assigned.</div>
                        `
                      : nothing
                  }
                </div>
              </div>

              ${
                channelsSnapshot
                  ? html`
                <div style="margin-bottom: 20px;">
                  <div style="font-size: 13px; font-weight: 600; margin-bottom: 10px;">Available in Instance</div>
                  <div class="chip-row" style="display: flex; flex-wrap: wrap; gap: 8px;">
                    ${Object.keys(channelsSnapshot.channels)
                      .filter((id) => !(e.channels?.allowed ?? []).includes(id))
                      .map(
                        (id) => html`
                      <button class="chip" style="cursor: pointer; border: 1px dashed var(--border); background: transparent; padding: 6px 12px; border-radius: 16px;" @click=${() => onAddChannel(id)}>
                        + ${channelsSnapshot.channelLabels?.[id] ?? id}
                      </button>
                    `,
                      )}
                  </div>
                </div>
              `
                  : nothing
              }

              <div style="display: flex; gap: 8px;">
                <input type="text" id="new-channel-id" placeholder="Enter custom channel ID..." style="flex: 1;" @keydown=${(
                  ev: KeyboardEvent,
                ) => {
                  if (ev.key === "Enter") {
                    const input = ev.target as HTMLInputElement;
                    if (input.value.trim()) {
                      onAddChannel(input.value.trim());
                      input.value = "";
                    }
                  }
                }} />
                <button class="btn primary" @click=${() => {
                  const input = document.getElementById("new-channel-id") as HTMLInputElement;
                  if (input.value.trim()) {
                    onAddChannel(input.value.trim());
                    input.value = "";
                  }
                }}>Add Custom</button>
              </div>
            </div>
          </div>
 
          <!-- Connected Accounts -->
          <div class="card" style="margin-bottom: 24px;">
            <div class="card-header">
              <h3 style="margin: 0; font-size: 16px;">Connected Accounts</h3>
            </div>
            <div class="card-body" style="padding: 20px;">
              <p class="muted" style="margin-bottom: 16px;">Attach provider credentials or auth profiles to this employee.</p>
              <div style="margin-bottom: 16px; padding: 10px; border: 1px dashed var(--border); border-radius: 8px; background: var(--secondary);">
                <div style="font-weight: 600; font-size: 12px; margin-bottom: 6px;">Guided setup</div>
                <ol style="margin: 0; padding-left: 16px; font-size: 12px; color: var(--muted);">
                  <li>Pick a provider to prefill defaults.</li>
                  <li>Click a quick-pick chip to auto-save.</li>
                  <li>Optionally edit fields and press Add Account.</li>
                </ol>
              </div>
              <div style="display: grid; gap: 12px; margin-bottom: 16px;">
                ${
                  accountEntries.length > 0
                    ? accountEntries.map(
                        ([provider, account]) => html`
                      <div style="display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 10px 12px; border: 1px solid var(--border); border-radius: 10px;">
                        <div>
                          <div style="font-weight: 600;">${provider}</div>
                          <div class="muted" style="font-size: 12px;">
                            ${account.profileId}${account.accountId ? ` • acct:${account.accountId}` : ""}${account.label ? ` • ${account.label}` : ""}${account.username ? ` • ${account.username}` : ""}
                          </div>
                        </div>
                        <button
                          class="btn-icon"
                          style="padding: 6px;"
                          @click=${() => {
                            const nextAccounts = { ...e.accounts };
                            delete nextAccounts[provider];
                            onUpdate({ accounts: nextAccounts });
                          }}
                        >
                          ✕
                        </button>
                      </div>
                    `,
                      )
                    : html`
                        <div class="muted">No accounts connected.</div>
                      `
                }
              </div>
              <div data-account-form style="display: grid; gap: 10px;">
                <input
                  type="text"
                  placeholder="Provider ID (e.g., github, whatsapp)"
                  list=${providerListId}
                  data-account-provider
                  @change=${(ev: Event) => {
                    const input = ev.currentTarget as HTMLInputElement;
                    const provider = input.value.trim();
                    if (!provider) {
                      return;
                    }
                    const container = input.closest<HTMLElement>("[data-account-form]");
                    const defaults = resolveDefaultsForProvider(provider);
                    applyAccountSelection(container, { provider, ...defaults });
                  }}
                />
                <datalist id=${providerListId}>
                  ${providerOptions.map((provider) => html`<option value=${provider}></option>`)}
                </datalist>
                <input
                  type="text"
                  placeholder="Account ID (optional for channels)"
                  list=${accountListId}
                  data-account-id
                  @change=${(ev: Event) => {
                    const input = ev.currentTarget as HTMLInputElement;
                    const container = input.closest<HTMLElement>("[data-account-form]");
                    const profileInput =
                      container?.querySelector<HTMLInputElement>("[data-profile-id]");
                    if (profileInput && !profileInput.value.trim()) {
                      profileInput.value = input.value.trim();
                    }
                  }}
                />
                <datalist id=${accountListId}>
                  ${channelAccounts.map(
                    (account) =>
                      html`<option value=${account.accountId} label=${`${account.label ?? account.channelId} (${account.channelId})`}></option>`,
                  )}
                </datalist>
                <input
                  type="text"
                  placeholder="Profile ID / credential reference"
                  list=${profileListId}
                  data-profile-id
                />
                <datalist id=${profileListId}>
                  ${authProfiles.map(
                    (profile) =>
                      html`<option value=${profile.profileId} label=${profile.label ?? profile.profileId}></option>`,
                  )}
                </datalist>
                <input type="text" placeholder="Label or username (optional)" data-account-label />
                <button class="btn primary" @click=${(ev: Event) => {
                  const container = (ev.currentTarget as HTMLButtonElement).closest(
                    "[data-account-form]",
                  );
                  const providerInput =
                    container?.querySelector<HTMLInputElement>("[data-account-provider]");
                  const accountInput =
                    container?.querySelector<HTMLInputElement>("[data-account-id]");
                  const profileInput =
                    container?.querySelector<HTMLInputElement>("[data-profile-id]");
                  const labelInput =
                    container?.querySelector<HTMLInputElement>("[data-account-label]");
                  const provider = providerInput?.value.trim() ?? "";
                  const accountId = accountInput?.value.trim() ?? "";
                  const profileId = profileInput?.value.trim() ?? accountId;
                  const label = labelInput?.value.trim() ?? "";
                  if (!provider || !profileId) {
                    return;
                  }
                  if (!validateAccountSelection({ provider, accountId, profileId })) {
                    return;
                  }
                  saveAccountBinding({ provider, accountId, profileId, label });
                  if (providerInput) {
                    providerInput.value = "";
                  }
                  if (accountInput) {
                    accountInput.value = "";
                  }
                  if (profileInput) {
                    profileInput.value = "";
                  }
                  if (labelInput) {
                    labelInput.value = "";
                  }
                }}>Add Account</button>
                ${
                  authProfiles.length > 0
                    ? html`
                      <div>
                        <div class="muted" style="font-size: 12px; margin-bottom: 6px;">Quick pick auth profile</div>
                        <div class="chip-row">
                          ${authProfiles.map((profile) => {
                            const provider = resolveProfileProvider(profile);
                            return html`
                              <button
                                class="chip"
                                style="cursor: pointer; border: 1px dashed var(--border); background: transparent; padding: 4px 10px;"
                                @click=${(ev: Event) => {
                                  const container = (
                                    ev.currentTarget as HTMLButtonElement
                                  ).closest<HTMLElement>("[data-account-form]");
                                  const selection = {
                                    provider,
                                    profileId: profile.profileId,
                                    label: profile.label ?? "",
                                    accountId: "",
                                  };
                                  applyAccountSelection(container, selection);
                                  saveAccountBinding(selection);
                                }}
                              >
                                ${profile.label ?? profile.profileId}
                              </button>
                            `;
                          })}
                        </div>
                      </div>
                    `
                    : nothing
                }
                ${
                  channelAccounts.length > 0
                    ? html`
                      <div>
                        <div class="muted" style="font-size: 12px; margin-bottom: 6px;">Quick pick channel account</div>
                        <div class="chip-row">
                          ${channelAccounts.map(
                            (account) => html`
                          <button
                            class="chip"
                            style="cursor: pointer; border: 1px dashed var(--border); background: transparent; padding: 4px 10px;"
                            @click=${(ev: Event) => {
                              const container = (
                                ev.currentTarget as HTMLButtonElement
                              ).closest<HTMLElement>("[data-account-form]");
                              const selection = {
                                provider: account.channelId,
                                accountId: account.accountId,
                                profileId: account.accountId,
                                label: account.label ?? "",
                              };
                              applyAccountSelection(container, selection);
                              saveAccountBinding(selection);
                            }}
                          >
                            ${account.label ?? account.channelId}:${account.accountId}${account.isDefault ? " (default)" : ""}
                          </button>
                        `,
                          )}
                      </div>
                    </div>
                  `
                    : nothing
                }
              </div>
              ${
                authProfileHints.length > 0
                  ? html`<div class="muted" style="font-size: 12px; margin-top: 8px;">Auth profiles: ${authProfileHints.join(", ")}</div>`
                  : nothing
              }
              ${
                channelAccountHints.length > 0
                  ? html`<div class="muted" style="font-size: 12px; margin-top: 4px;">Channel accounts: ${channelAccountHints.join(", ")}</div>`
                  : nothing
              }
            </div>
          </div>
 
          <!-- Calendar & Email Access -->
          <div class="card" style="margin-bottom: 24px;">
            <div class="card-header">
              <h3 style="margin: 0; font-size: 16px;">Abstract Access Control</h3>
            </div>
            <div class="card-body" style="padding: 20px;">
              <p class="muted" style="margin-bottom: 16px;">Configure high-level access to corporate services.</p>
              
              <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 24px;">
                <div>
                  <div style="font-weight: 600; font-size: 14px; margin-bottom: 12px; display: flex; align-items: center; gap: 8px;">
                    📅 Calendar Access
                  </div>
                  <div class="field checkbox">
                    <input type="checkbox" ?checked=${(e.metadata?.calendarAccess as { enabled?: boolean } | undefined)?.enabled ?? false} @change=${(
                      ev: Event,
                    ) => {
                      const enabled = (ev.target as HTMLInputElement).checked;
                      onUpdate({
                        metadata: {
                          ...e.metadata,
                          calendarAccess: {
                            ...(e.metadata?.calendarAccess as Record<string, unknown> | undefined),
                            enabled,
                          },
                        },
                      });
                    }} />
                    <span>Enable Calendar Sync</span>
                  </div>
                  <div class="field" style="margin-top: 12px;">
                    <label>Calendar Identifier</label>
                    <input type="text" placeholder="e.g. primary, work-cal" .value=${(e.metadata?.calendarAccess as { id?: string } | undefined)?.id ?? ""} @change=${(
                      ev: Event,
                    ) => {
                      const id = (ev.target as HTMLInputElement).value;
                      onUpdate({
                        metadata: {
                          ...e.metadata,
                          calendarAccess: {
                            ...(e.metadata?.calendarAccess as Record<string, unknown> | undefined),
                            id,
                          },
                        },
                      });
                    }} />
                  </div>
                </div>

                <div>
                  <div style="font-weight: 600; font-size: 14px; margin-bottom: 12px; display: flex; align-items: center; gap: 8px;">
                    ✉️ Email Access
                  </div>
                  <div class="field checkbox">
                    <input type="checkbox" ?checked=${(e.metadata?.emailAccess as { enabled?: boolean } | undefined)?.enabled ?? false} @change=${(
                      ev: Event,
                    ) => {
                      const enabled = (ev.target as HTMLInputElement).checked;
                      onUpdate({
                        metadata: {
                          ...e.metadata,
                          emailAccess: {
                            ...(e.metadata?.emailAccess as Record<string, unknown> | undefined),
                            enabled,
                          },
                        },
                      });
                    }} />
                    <span>Enable Email Processing</span>
                  </div>
                  <div class="field" style="margin-top: 12px;">
                    <label>Email Address / Alias</label>
                    <input type="text" placeholder="e.g. assistant@company.com" .value=${(e.metadata?.emailAccess as { address?: string } | undefined)?.address ?? ""} @change=${(
                      ev: Event,
                    ) => {
                      const address = (ev.target as HTMLInputElement).value;
                      onUpdate({
                        metadata: {
                          ...e.metadata,
                          emailAccess: {
                            ...(e.metadata?.emailAccess as Record<string, unknown> | undefined),
                            address,
                          },
                        },
                      });
                    }} />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Skills & Prompts -->
          <div class="card">
            <div class="card-header">
              <h3 style="margin: 0; font-size: 16px;">Skills & Permissions</h3>
            </div>
            <div class="card-body" style="padding: 20px;">
              <p class="muted" style="margin-bottom: 16px;">Enable or disable specific skills for this virtual employee.</p>
              
              ${
                skillsReport
                  ? html`
                <div class="skills-list" style="display: grid; gap: 12px;">
                  ${skillsReport.skills.map((skill) => {
                    const message = skillMessages[skill.skillKey];
                    return html`
                      <div class="list-item" style="padding: 12px; border: 1px solid var(--border); border-radius: 8px; display: flex; justify-content: space-between; align-items: center;">
                        <div>
                          <div style="font-weight: 600;">${skill.emoji ? `${skill.emoji} ` : ""}${skill.name}</div>
                          <div class="muted" style="font-size: 12px;">${skill.description}</div>
                          ${message ? html`<div style="font-size: 11px; margin-top: 4px; color: ${message.kind === "error" ? "var(--danger)" : "var(--ok)"}">${message.message}</div>` : nothing}
                        </div>
                        <button class="btn btn--sm ${skill.disabled ? "" : "primary"}" @click=${() => onToggleSkill(skill.skillKey, skill.disabled)}>
                          ${skill.disabled ? "Enable" : "Disable"}
                        </button>
                      </div>
                    `;
                  })}
                </div>
              `
                  : html`
                      <div class="callout info">Loading skills...</div>
                    `
              }
              
              <div class="callout info" style="margin-top: 20px;">
                System prompts and advanced agent configuration can be managed in the <a href="/config">Config</a> tab.
              </div>
            </div>
          </div>
        </div>

        <div class="employee-detail-sidebar">
          <div class="card">
            <div class="card-header">
              <h3 style="margin: 0; font-size: 16px;">Employee Stats</h3>
            </div>
            <div class="card-body" style="padding: 20px;">
              <div class="stat-item" style="margin-bottom: 16px;">
                <div class="muted" style="font-size: 12px; text-transform: uppercase;">Status</div>
                <div style="font-weight: 600; color: ${getStatusColor(e.status)};">${e.status.toUpperCase()}</div>
              </div>
              <div class="stat-item" style="margin-bottom: 16px;">
                <div class="muted" style="font-size: 12px; text-transform: uppercase;">Location</div>
                <input type="text" .value=${e.location ?? ""} style="width: 100%; margin-top: 4px;" @change=${(ev: Event) => onUpdate({ location: (ev.target as HTMLInputElement).value || undefined })} />
              </div>
              <div class="stat-item">
                <div class="muted" style="font-size: 12px; text-transform: uppercase;">Cost Center</div>
                <input type="text" .value=${e.costCenter ?? ""} style="width: 100%; margin-top: 4px;" @change=${(ev: Event) => onUpdate({ costCenter: (ev.target as HTMLInputElement).value || undefined })} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  `;
}

function getStatusColor(status: string): string {
  switch (status) {
    case "active":
      return "var(--ok)";
    case "onboarding":
      return "var(--warn)";
    case "terminated":
      return "var(--danger)";
    default:
      return "var(--muted)";
  }
}
